<template>
    <div>
        <div class="search-wrapper">
            <b-container class="pt-3 pb-2">
                <Search :search="search" @update-search="updateSearch" />
            </b-container>
        </div>
        <div class="search-results">
            <b-container>
                <CovidAlert/>
                <div class="inner">
                    <div class="p-4">
                        <router-link :to="{ path: '/' + $i18n.locale + '/hotels', query: $route.query }">
                            <font-awesome-icon :icon="['fas', 'arrow-left']" />
                            See all properties
                        </router-link>
                    </div>
                </div>
                <div class="errors" v-if="errors.length">
                    <div v-for="error in errors" :key="error">
                        <b-alert class="mt-4 mb-4" show variant="danger">
                            {{ error.code }} {{ error.title }}
                        </b-alert >
                    </div>
                    <div class="mb-5">
                        <FeaturedHotels :search="search" />
                    </div>
                </div>
                <div v-else-if="result.hotel">
                    <HotelDetailHeader :result="result" :partners="partners" />
                    <div class="inner section-menu-wrap mb-4 position-sticky top-0">
                        <b-nav class="section-menu p-1">
                            <b-nav-item href="#overview" @click="scrollIntoView">Overview</b-nav-item>
                            <b-nav-item href="#rooms" @click="scrollIntoView">Rooms</b-nav-item>
                            <b-nav-item href="#location" @click="scrollIntoView">Location</b-nav-item>
                            <b-nav-item href="#amenities" @click="scrollIntoView">Amenities</b-nav-item>
                            <!-- <b-nav-item href="#policies" @click="scrollIntoView">Policies</b-nav-item> -->
                            <b-nav-item href="#reviews" @click="scrollIntoView">Reviews</b-nav-item>
                            <b-navbar-nav class="ml-auto mt-1">
                                <b-button href="#rooms" variant="primary" size="sm" @click="scrollIntoView">
                                    Reserve a Room
                                </b-button>
                            </b-navbar-nav>
                        </b-nav>
                    </div>
                    <div id="nav-scroller">
                        <div class="mb-4" id="overview">
                            <b-row class="row-eq-height">
                                <b-col md="8">
                                    <div class="inner p-4 h-100">
                                        <StarRating :result="result" />
                                        <h1 class="display-1">{{ result.hotel.name }}</h1>
                                        <template v-if="result.hotel.description">
                                            <p>{{ result.hotel.description.text }}</p>
                                            <hr/>
                                        </template>
                                        <b-row>
                                            <b-col lg="8" class="mb-3">
                                                <h3 class="display-4">Popular amenities</h3>
                                                <b-row v-if="popularAmenities.length">
                                                    <b-col v-for="amenity in popularAmenities" :key="amenity" sm="6" class="mb-2">
                                                        <font-awesome-icon v-if="amenities_popular_mapping[amenity]" :icon="['fas', amenities_popular_mapping[amenity]]" class="pop-amenity-icon" />
                                                        {{ $t(amenity) }}
                                                    </b-col>
                                                </b-row>
                                                <a href="#amenities" @click="scrollIntoView">
                                                    See all amenities >>
                                                </a>
                                            </b-col>
                                            <b-col lg="4" class="mb-3" v-if="sentiment">
                                                <h3 class="display-4">Guest rating</h3>
                                                <Rating :sentiment="sentiment" class="mb-2" />
                                                <a href="#reviews" @click="scrollIntoView">
                                                    See all reviews >>
                                                </a>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </b-col>
                                <b-col md="4">
                                    <div class="inner p-4 h-100">
                                        <div class="mb-4">
                                            <!--
                                            <b-button class="mb-4" variant="primary" block size="sm" href="https://stay.activehosted.com/f/8" target="_blank">
                                                <font-awesome-icon :icon="['fas', 'building']" class="mr-1" />
                                                Claim this hotel
                                            </b-button>
                                            -->
                                            <a :href="'https://www.google.com/maps/search/?api=1&amp;query=' + result.hotel.latitude + ',' + result.hotel.longitude" target="_blank">
                                                <GmapMap
                                                :center="{lat: result.hotel.latitude, lng: result.hotel.longitude}"
                                                :zoom="13"
                                                :options="{scrollwheel: false, disableDefaultUI:true, gestureHandling:'none', draggableCursor:'pointer', clickableIcons:false}"
                                                class="maps mb-1"
                                                >
                                                    <GmapMarker
                                                        :position="{ lat: result.hotel.latitude, lng: result.hotel.longitude }"
                                                    />
                                                </GmapMap>
                                            </a>
                                            <address class="mb-2">{{ capitalize(result.hotel.address.lines.join(', ')) }}, {{ capitalize(result.hotel.address.cityName) }}, {{ result.hotel.address.postalCode }}</address>
                                            <a :href="'https://www.google.com/maps/search/?api=1&amp;query=' + result.hotel.latitude + ',' + result.hotel.longitude" target="_blank">
                                                View in a map >
                                            </a>
                                        </div>
                                        <hr/>
                                        <PoiList :result="result" class="mb-4" />
                                    </div>
                                </b-col>
                            </b-row>
                        </div>
                        
                        <div class="inner p-4 mb-4" id="choose">
                            <h3 class="display-3 mb-4">{{ $t('Choose your room') }}</h3>
                            <SearchRefine :search="search" :result="result" @update-search="updateSearch" />
                        </div>

                        <div class="inner mb-4 p-4" id="rooms">
                            <template v-for="(group, idx) in sortedOffers">
                                <template v-for="(beds, idb) in group">
                                    <div v-for="(number, idn) in beds" :key="idn" class="room-group mb-4">
                                        <b-row class="rooms">
                                            <b-col md="4">
                                                <h3 class="display-3 position-sticky mb-4">
                                                    {{ $t(idx) }}
                                                    <span class="badge badge-primary">
                                                        <small>
                                                            {{ idn }}
                                                            {{ $t(idb) }} {{ $tc('bed', idn) }}
                                                        </small>
                                                    </span>
                                                </h3>
                                            </b-col>
                                            <b-col class="mb-4" md="8">
                                                <b-card bg-variant="light" border-variant="light" v-for="(offer, ida) in number" :key="ida" class="mb-2">
                                                    <b-row class="room" align-v="center">
                                                        <b-col sm="5">
                                                            <Room :offer="offer" />
                                                        </b-col>
                                                        <b-col sm="4" class="text-md-right">
                                                            <Price :exchangerates="exchangerates" :offer="offer" />
                                                        </b-col> 
                                                        <b-col sm="3" class="text-md-right">
                                                            <b-button variant="primary" :to="{ path: '/' + $i18n.locale + '/book/' +  convertToSlug(result.hotel.name) + '/' + toHex(result.hotel.hotelId) + '/' + toHex(offer.id), query: $route.query }" >
                                                                {{ $t('Reserve now') }} >
                                                            </b-button>
                                                        </b-col>
                                                    </b-row>
                                                </b-card>
                                            </b-col>
                                        </b-row>
                                    </div>
                                </template>
                            </template>
                        </div>

                        <PartnerDeals :partners="partners" :search="search" />

                        <FeaturedActivities :result="result" />

                        <Amenities :amenities="amenities" />

                        <!--
                        <div class="inner mb-5" id="policies">
                            <div class="p-4">
                                <b-row>
                                    <b-col md="4">
                                        <h3 class="display-3 position-sticky mb-4">Policies</h3>
                                    </b-col>
                                    <b-col md="8">
                                        {{ result }}
                                    </b-col>
                                </b-row>
                            </div>
                        </div>
                        -->

                        <div v-if="sentiment" class="inner mb-5" id="reviews">
                            <div class="p-4">
                                <b-row>
                                    <b-col md="4">
                                        <h3 class="display-3 position-sticky">
                                            {{ $t('Guest rating') }}
                                        </h3>
                                    </b-col>
                                    <b-col md="8">
                                        <b-row>
                                            <b-col lg="4">
                                                <Rating :sentiment="sentiment" />
                                            </b-col>
                                            <b-col lg="8" v-if="sentiment.sentiments">
                                                <Sentiment :sentiment="sentiment"/>
                                            </b-col>
                                        </b-row>
                                    </b-col>
                                </b-row>
                            </div>
                        </div>
                        <div class="mb-5" id="featured">
                            <FeaturedHotels :search="search" />
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="inner p-5 mb-4">
                        <content-placeholders-img style="width:100%; height:250px;" />
                    </div>
                    <div class="inner mb-5">
                        <b-row>
                            <b-col md="8">
                                <div class="p-5">
                                    <content-placeholders :rounded="true">
                                        <content-placeholders-heading/>
                                        <content-placeholders-text :lines="4"/>
                                    </content-placeholders>
                                    <content-placeholders :rounded="true">
                                        <content-placeholders-heading/>
                                        <content-placeholders-text :lines="4"/>
                                    </content-placeholders>
                                </div>
                            </b-col>
                            <b-col md="4">
                                <div class="p-5">
                                    <content-placeholders :rounded="true">
                                        <content-placeholders-img/>
                                        <content-placeholders-text :lines="1"/>
                                    </content-placeholders>
                                    <content-placeholders :rounded="true">
                                        <content-placeholders-heading/>
                                        <content-placeholders-text :lines="5"/>
                                    </content-placeholders>
                                </div>
                            </b-col>
                        </b-row>
                    </div>
                    <div class="inner p-5 mb-5">
                        <content-placeholders :rounded="true">
                            <content-placeholders-heading/>
                            <content-placeholders-text :lines="4"/>
                        </content-placeholders>
                    </div>
                </div>
            </b-container>
        </div>
    </div>
</template>

<script>
    import axios from "axios"
    import Search from '@/components/ui/Search'
    import SearchRefine from '@/components/ui/SearchRefine'
    import Price from '@/components/ui/Price'
    import Room from '@/components/ui/Room'
    import CovidAlert from '@/components/ui/CovidAlert'
    import Rating from '@/components/ui/Rating'
    import PoiList from '@/components/ui/PoiList'
    import StarRating from '@/components/ui/StarRating'
    import FeaturedHotels from '@/components/ui/FeaturedHotels'
    import FeaturedActivities from '@/components/ui/FeaturedActivities'
    import PartnerDeals from '@/components/ui/PartnerDeals'
    import HotelDetailHeader from '@/components/ui/HotelDetailHeader'
    import Amenities from '@/components/ui/Amenities'
    import Sentiment from '@/components/ui/Sentiment'

    import { library } from '@fortawesome/fontawesome-svg-core'
    import { 
        faArrowLeft, 
        faWifi, 
        faSuitcase, 
        faParking, 
        faGlassCheers,
        faTicketAlt,
        faUsers,
        faConciergeBell,
        faBuilding,
        faCheck,
        faTv,
        faBath,
        faBed,
        faDog,
        faLock,
    } from '@fortawesome/free-solid-svg-icons'

    import { faAccessibleIcon } from '@fortawesome/free-brands-svg-icons'

    library.add(faArrowLeft)
    library.add(faWifi)
    library.add(faSuitcase)
    library.add(faParking)
    library.add(faGlassCheers)
    library.add(faAccessibleIcon)
    library.add(faTicketAlt);
    library.add(faUsers);
    library.add(faConciergeBell)
    library.add(faBuilding)
    library.add(faCheck)
    library.add(faTv)
    library.add(faBath)
    library.add(faBed)
    library.add(faDog)
    library.add(faLock)

    export default {
        components: {
            CovidAlert,
            Search,
            SearchRefine,
            Room,
            Price,
            PartnerDeals,
            Rating,
            PoiList,
            StarRating,
            FeaturedHotels,
            FeaturedActivities,
            HotelDetailHeader,
            Amenities,
            Sentiment,
        },
        computed: {
            sortedOffers: function() {
                var results = {};

                for (const offer in this.result.offers) {
                    var item = this.result.offers[offer];
                    var detail = this.roomDetails(item);
                    
                    //var detail = true;
                    //var item = this.result.offers[offer];
                    //var category = item.room.typeEstimated.category;

                    if (detail) {
                        var category = detail.room_type.type;
                        var bed = detail.bed_type.type;
                        var number = detail.number_of_beds;

                        if( !results[category] ) {
                            results[category] = {};
                        }

                        if( !results[category][bed] ) {
                            results[category][bed] = {};
                        }

                        if( !results[category][bed][number] ) {
                            results[category][bed][number] = [];
                        }

                        results[category][bed][number].push(item);
                    }
                }

                return results;
            },
            popularAmenities: function () {
                var results = [];

                for (const amenity in this.amenities) {
                    for (const item in this.amenities[amenity]) {
                        for (const pop in this.amenities_popular_mapping) {
                            if (pop == this.amenities[amenity][item]) {
                                results.push(pop)
                            }
                        }
                    }
                }

                return results;
            }
        },
        data () {
            return {
                search: {
                    place: {
                        name: '',
                        lat: '',
                        lng: '',
                    },
                    date: {
                        checkin: '',
                        checkout: '',
                    },
                    occupation: {
                        adults: 0,
                        rooms: 0,
                        children: 0,
                        ages: [],
                    },
                    sort: '',
                    currency: '',
                    page: 1,
                },
                done: true,
                result: [],
                exchangerates: {},
                partners: {},
                errors: [],
                sentiment: null,
                amenities: {},
                amenities_popular_mapping: {
                    'SWIMMING_POOL': 'ticket-alt',
                    'FREE_INTERNET': 'wifi',
                    'AIR_CONDITIONING': 'bed',
                    'RESTAURANT': 'glass-cheers',
                    'ROOM_SERVICE': 'concierge-bell',
                    '24_HOUR_FRONT_DESK': 'concierge-bell',
                    'PETS_ALLOWED': 'dog',
                    'SERVICE_DOGS_ALLOWED': 'dog',
                    'FITNESS_CENTER': 'ticket-alt',
                    'INDOOR_POOL': 'ticket-alt',
                    'SAUNA': 'ticket-alt',
                    'BUSINESS_CENTER': 'suitcase',
                    'PARKING': 'parking',
                    'CONFERENCE_FACILITIES': 'suitcase',
                },
                amenities_mapping: {
                    'WI-FI_IN_ROOM': 'room_bedroom',
                    'HIGH_SPEED_INTERNET_IN_ROOM': 'room_bedroom',
                    'WIRELESS_CONNECTIVITY': 'internet',
                    'GARAGE_PARKING': 'parking_transportation',
                    'PARKING': 'parking_transportation',
                    'ICE_MACHINES': 'food_drink',
                    'RESTAURANT': 'food_drink',
                    'LOUNGE': 'food_drink',
                    'SWIMMING_POOL': 'thingstodo',
                    'ELEVATOR': 'conveniences',
                    'ROOM_SERVICE': 'room_bedroom',
                    'CONCIERGE': 'guestservices',
                    '24_HOUR_FRONT_DESK': 'guestservices',
                    'WAKEUP_SERVICE': 'guestservices',
                    'MEETING_FACILITIES': 'businessservices',
                    'PHOTOCOPIER': 'businessservices',
                    'AUDIO-VISUAL_EQUIPMENT': 'businessservices',
                    'OVERHEAD_PROJECTOR': 'businessservices',
                    'ACCESSIBLE_FACILITIES': 'accessibility',
                    'ACCESSIBLE_BATHS': 'accessibility',
                    'WHEELCHAIR_ACCESSIBLE_PUBLIC_AREA': 'accessibility',
                    'DISABLED_FACILITIES': 'accessibility',
                    'ALARM_CLOCK': 'room_bedroom',
                    'AIR_CONDITIONING': 'room_bedroom',
                    'NON_SMOKING_ROOMS': 'room_bedroom',
                    '120_AC': 'room_more',
                    '220_AC': 'room_more',
                    'HAIR_DRYER': 'room_bathroom',
                    'BATH': 'room_bathroom',
                    'SHOWER': 'room_bathroom',
                    'TELEVISION': 'room_entertainment',
                    'IRON/IRON_BOARD': 'room_more',
                    'DIRECT_DIAL_PHONE': 'room_more',
                    'CORDLESS_PHONE': 'room_more',
                    'HEATED_POOL': 'thingstodo',
                    'VALET_PARKING': 'parking_transportation',
                    'INTERNET_SERVICES': 'internet',
                    'GIFT_SHOP': 'thingstodo',
                    'PETS_ALLOWED': 'pets',
                    'TRANSLATION_SERVICES': 'businessservices',
                    'KIDS_WELCOME': 'more',
                    'BUS_PARKING': 'parking_transportation',
                    'CONNECTING_ROOMS': 'room_bedroom',
                    'PORTER/BELLBOY': 'conveniences',
                    'SMOKE_DETECTOR': 'safetysecurity',
                    'FIRE_SAFETY': 'safetysecurity',
                    'FIRE_DETECTORS': 'safetysecurity',
                    'EMERGENCY_LIGHTING': 'safetysecurity',
                    'INDOOR_POOL': 'thingstodo',
                    'SECRETARIAL_SERVICES': 'businessservices',
                    'MEETING_ROOMS': 'businessservices',
                    'BUSINESS_CENTER': 'businessservices',
                    'CRIBS_AVAILABLE': 'room_bedroom',
                    'VOICEMAIL_IN_ROOM': 'room_bedroom',
                    'CABLE_TELEVISION': 'room_entertainment',
                    'SATELLITE_TV': 'room_entertainment',
                    'SAFE': 'room_bedroom',
                    'BREAKFAST_FULL': 'room_bedroom',
                    'SPA': 'thingstodo',
                    'EXPRESS_CHECK_IN': 'guestservices',
                    'EXPRESS_CHECK_OUT': 'guestservices',
                    'INTERIOR_ROOM_ENTRY': 'room_bedroom',
                    'SERVICE_DOGS_ALLOWED': 'pets',
                    'DISABLED_ACCESSIBLE_TOILETS': 'accessibility',
                    'HANDRAILS_BATHROOM': 'room_bathroom',
                    'SAFE_DEPOSIT_BOX': 'businessservices',
                    'EXCHANGE_FACILITIES': 'conveniences',
                    'DRY_CLEANING': 'conveniences',
                    'LAUNDRY_SERVICE': 'conveniences',
                    'FRONT_DESK': 'conveniences',
                    'FLORIST': 'conveniences',
                    'CAR_RENTAL': 'parking_transportation',
                    'WEDDING_SERVICES': 'guestservices',
                    'GYM': 'thingstodo',
                    'HIGH_SPEED_INTERNET': 'internet',
                    'FREE_INTERNET': 'internet',
                    'HIGH_SPEED_WIRELESS': 'internet',
                    'PRINTER': 'businessservices',
                    'TEA/COFFEE_MAKER': 'room_bedroom',
                    'MINIBAR': 'room_entertainment',
                    'ROLLAWAY_BEDS': 'room_bedroom',
                    'OUTLET_ADAPTERS': 'room_entertainment',
                    'MOVIE_CHANNELS': 'room_entertainment',
                    'FITNESS_CENTER': 'thingstodo',
                    'HORSE_RIDING': 'thingstodo',
                    '24_HOUR_ROOM_SERVICE': 'room_bedroom',
                    'AIRLINE_DESK': 'guestservices',
                    'ATM/CASH_MACHINE': 'conveniences',
                    'BABY-SITTING': 'guestservices',
                    'COFFEE_SHOP': 'food_drink',
                    'CONFERENCE_FACILITIES': 'businessservices',
                    'DOCTOR_ON_CALL': 'guestservices',
                    'WHEELCHAIR_ACCESSIBLE_ROOM': 'accessibility',
                    'JOGGING_TRACK': 'thingstodo',
                    'ON-SITE_PARKING': 'parking_transportation',
                    'TOUR_DESK': 'thingstodo',
                    'WIFI': 'internet',
                    'NURSERY': 'guestservices',
                    'FEMA_FIRE_SAFETY_COMPLIANT': 'safetysecurity',
                    'EMERGENCY_BACKUP_GENERATOR': 'safetysecurity',
                    'DISCO': 'thingstodo',
                    'NIGHT_CLUB': 'thingstodo',
                    'MULTILINGUAL_STAFF': 'conveniences',
                    'HANDICAP_FACILITIES': 'accessibility',
                    'ONSITE_LAUNDRY': 'conveniences',
                    'WIDE_ENTRANCE': 'accessibility',
                    'FIRST_AID_STAF': 'safetysecurity',
                    'FREE_NEWSPAPER': 'guestservices',
                    'TRAVEL_AGENCY': 'guestservices',
                    'SAUNA': 'thingstodo',
                    'EXECUTIVE_FLOOR': 'businessservices',
                    'ACCESSIBLE_PARKING': 'accessibility',
                    'MASSAGE': 'thingstodo',
                    'SOLARIUM': 'conveniences',
                    'CONVENIENCE_STORE': 'conveniences',
                    'CHILDRENS_PLAY_AREA': 'conveniences',
                    'LUGGAGE_SERVICE': 'guestservices',
                    'HAIRDRESSER': 'guestservices',
                    'BAGGAGE_STORAGE': 'guestservices',
                    'FREE_AIRPORT_SHUTTLE': 'parking_transportation',
                    'BANQUETING_FACILITIES': 'conveniences',
                    'EMERGENCY_PLAN_FOR_DISABLED': 'accessibility',
                    'BRAILLE/LARGE_PRINT_MENU': 'accessibility',
                    'DISABLED_TRAINED_STAFF': 'accessibility',
                    'SPRINKLERS': 'safetysecurity',
                    'VIDEO_SURVEILANCE': 'safetysecurity',
                    'EXTINGUISHERS': 'safetysecurity',
                    'SIGHTSEEING': 'thingstodo',
                    'PC_HOOKUP_IN_ROOM': 'room_entertainment',
                    'TRANSPORTATION': 'parking_transportation',
                    'COMPUTER_RENTAL': 'businessservices',
                    'WOMENS_GUEST_ROOMS': 'conveniences',
                    'NO_PORN_FILMS': 'more',
                    'WHEELCHAIR_ACCESSIBLE_WASHBASIN': 'accessibility',
                    'WHEELCHAIR_ACCESSIBLE_LIGHT_SWITCH': 'accessibility',
                    'WHEELCHAIR_ACCESSIBLE_ELEVATORS': 'accessibility',
                    'ACCESSIBLE_BATH_CONTROLS': 'accessibility',
                    'NO_KID_ALLOWED': 'conveniences',
                    'SECURITY_GUARD': "safetysecurity",
                    'MAID_SERVICE': "guestservices",
                    'OUTDOOR_POOL': "thingstodo",
                    'ADAPT_ROOM_DOORS': 'room_bedroom',
                    'SPECIAL_NEEDS_MENU': 'food_drink',
                    'WIDE_CORRIDORS': 'accessibility',
                    'INTERNET_HOTSPOTS': 'internet',
                    'GAMES_ROOM': 'thingstodo',
                    'WIDE_RESTAURANT_ENTRANCE': 'accessibility',
                    'GOLF': 'thingstodo',
                    'BOATING': 'thingstodo',
                    'LCD/PROJECTOR': 'businessservices',
                    'CONFERENCE_SUITE': 'businessservices',
                    'CONVENTION_CENTRE': 'businessservices',
                    'LAUNDRY_EQUIPMENT_IN_ROOM': 'room_bedroom',
                    'FREE_HIGH_SPEED_INTERNET_IN_ROOM': 'room_bedroom',
                    'BOWLING': 'thingstodo',
                    'FIRE_SAFETY_NOT_STANDARD': 'safetysecurity',
                    'ADAPTED_PHONES': 'accessibility',
                    'TV_SUBTITLES/CAPTION': 'accessibility',
                    'EMERGENCY_CORD/BUTTON_BATHROOM': 'accessibility',
                    'HEARING_INDUCTION_LOOPS': 'accessibility',
                    'VIBRATING_PILLOWS_AVAILABLE': 'accessibility',
                    'BRAILLE/LARGE_PRINT_LITERATURE': 'accessibility',
                    'BABY_LISTENING_DEVICE': 'conveniences',
                    'BIDET': 'room_badroom',
                    'BEACH': 'thingstodo',
                    'FISHING': 'thingstodo',
                    'ROOMS_WITH_BALCONIES': 'room_bedroom',
                    'GUARDED_PARKING': 'parking_transportation',
                    'RESTRICTED_PUBLIC_ACCESS': 'conveniences',
                    'EXTERIOR_ROOM_ENTRY': 'room_bedroom',
                    'WHITE/BLACKBOARD': 'businessservices',
                    'CELLULAR_PHONE_RENTAL': 'conveniences',
                    'EXECUTIVE_DESK': 'businessservices',
                    'DVD/VIDEO_RENTAL': 'room_entertainment',
                },
                room_type_mapping: {
                    'A': {
                        'type': 'SUPERIOR_ROOM',
                        'desr': 'ROOM_WITH_BATH',
                    },
                    'B': {
                        'type': 'MODERATE_ROOM',
                        'desr': 'ROOM_WITH_BATH',
                    },
                    'C': {
                        'type': 'STANDARD_ROOM',
                        'desr': 'ROOM_WITH_BATH',
                    },
                    'D': {
                        'type': 'MINIMUM_ROOM',
                        'desr': 'ROOM_WITH_BATH',
                    },
                    'E': {
                        'type': 'SUPERIOR_ROOM',
                        'desr': 'ROOM_WITH_SHOWER',
                    },
                    'F': {
                        'type': 'MODERATE_ROOM',
                        'desr': 'ROOM_WITH_SHOWER',
                    },
                    'G': {
                        'type': 'STANDARD_ROOM',
                        'desr': 'ROOM_WITH_SHOWER',
                    },
                    'H': {
                        'type': 'MINIMUM_ROOM',
                        'desr': 'ROOM_WITH_SHOWER',
                    },
                    'I': {
                        'type': 'SUPERIOR_ROOM',
                        'desr': 'ROOM_WITHOUT_BATH_SHOWER',
                    },
                    'J': {
                        'type': 'MODERATE_ROOM',
                        'desr': 'ROOM_WITHOUT_BATH_SHOWER',
                    },
                    'K': {
                        'type': 'STANDARD_ROOM',
                        'desr': 'ROOM_WITHOUT_BATH_SHOWER',
                    },
                    'N': {
                        'type': 'NON_SMOKING_ROOM',
                        'desr': '',
                    },
                    'P': {
                        'type': 'EXECUTIVE_SUITE',
                        'desr': '',
                    },
                    'S': {
                        'type': 'SUITE',
                        'desr': '',
                    },
                    'T': {
                        'type': 'SUITE',
                        'desr': '',
                    },
                    'U': {
                        'type': 'SUITE',
                        'desr': '',
                    },
                    'W': {
                        'type': 'ROOM_AT_WEEKEND_RATE',
                        'desr': '',
                    },
                    '*': {
                        'type': 'RUN_OF_HOUSE_ROOM',
                        'desr': '',
                    },
                },
                bed_type_mapping: {
                    'T': {
                        'type': 'TWIN',
                        'persons': 2,
                    },
                    'S': {
                        'type': 'SINGLE',
                        'persons': 1,
                    },
                    'D': {
                        'type': 'DOUBLE',
                        'persons': 2,
                    },
                    'K': {
                        'type': 'KING',
                        'persons': 2,
                    },
                    'Q': {
                        'type': 'QUEEN',
                        'persons': 2,
                    },
                    'W': {
                        'type': 'WATER',
                        'persons': 2,
                    },
                    'P': {
                        'type': 'PULL-OUT',
                        'persons': 1,
                    },
                    '*': {
                        'type': 'RUN_OF_HOUSE_BED',
                        'persons': 1,
                    },
                },
            }
        },
        mounted () {
            this.search.place.name = (this.$route.query.search ? this.$route.query.search : null);
            this.search.place.lat = (this.$route.query.lat ? this.$route.query.lat : null);
            this.search.place.lng = (this.$route.query.lng ? this.$route.query.lng : null);
            this.search.date.checkin = (this.$route.query.checkin ? this.$route.query.checkin : null);
            this.search.date.checkout = (this.$route.query.checkout ? this.$route.query.checkout : null);
            this.search.occupation.adults = (this.$route.query.adults ? parseInt(this.$route.query.adults) : 0);
            this.search.occupation.children = (this.$route.query.children ? parseInt(this.$route.query.children) : 0);
            this.search.occupation.rooms = (this.$route.query.rooms ? parseInt(this.$route.query.rooms) : 0);
            this.search.occupation.ages = (this.$route.query.ages ? JSON.parse(this.$route.query.ages) : []);
            this.search.currency = (this.$route.query.currency ? this.$route.query.currency : 'EUR');
            this.search.sort = (this.$route.query.sort ? this.$route.query.sort : null);
            this.search.page = (this.$route.query.page ? this.$route.query.page : 1);
            this.loadResults();
        },
        methods: {
            scrollIntoView(event) {
                event.preventDefault()
                const href = event.target.getAttribute('href');
                const el = href ? document.querySelector(href) : null;

                if (el) {
                    el.scrollIntoView({behavior: "smooth", block: "start"});
                }
            },
            updateSearch(search) {
                this.search = search;
                this.loadResults();
            },
            loadResults() {

                this.result = [];
                this.amenities = {};

                let self = this;

                axios
                    .get('https://api.stay.com.de/?action=hotel-detail' +
                        '&currency=' + this.search.currency +
                        '&id=' + this.fromHex(this.$route.params.id) +
                        '&lat=' + this.search.place.lat + 
                        '&lng=' + this.search.place.lng +
                        '&checkin=' + this.search.date.checkin +
                        '&checkout=' + this.search.date.checkout +
                        '&adults=' + this.search.occupation.adults +
                        '&children=' + this.search.occupation.children +
                        '&ages=' + JSON.stringify(this.search.occupation.ages) +
                        '&rooms=' + this.search.occupation.rooms +
                        '&mode=' + process.env.VUE_APP_MODE
                    )
                    .then(response => {

                        self.exchangerates = {};

                        if (response.data.dictionaries) {
                            if (response.data.dictionaries.currencyConversionLookupRates) {
                                self.exchangerates = response.data.dictionaries.currencyConversionLookupRates;
                            }
                        }
                        
                        if (response.data.data) {
                            self.result = response.data.data;

                            axios
                                .get('https://api.stay.com.de/?action=hotel-compare' +
                                    '&currency=' + this.search.currency +
                                    '&search=' + self.result.hotel.name + 
                                    '&city=' + self.result.hotel.address.cityName +
                                    '&checkin=' + this.search.date.checkin +
                                    '&checkout=' + this.search.date.checkout +
                                    '&adults=' + this.search.occupation.adults +
                                    '&children=' + this.search.occupation.children +
                                    '&ages=' + JSON.stringify(this.search.occupation.ages) +
                                    '&rooms=' + this.search.occupation.rooms +
                                    '&mode=' + process.env.VUE_APP_MODE
                                )
                                .then(response => {
                                    if (response.data) {
                                        self.partners = response.data;
                                    }
                                })
                            ;

                            if (self.result.hotel.amenities.length) {
                                var mapping = self.amenities_mapping;
                                var amenities = self.amenities;

                                if (amenities) {
                                    self.result.hotel.amenities.forEach(function(value) {
                                        var key = mapping[value];

                                        if ( !amenities[key] ) {
                                            amenities[key] = [];
                                        }

                                        if (key && key in amenities) {
                                            if (!amenities[key].includes(value)) {
                                                amenities[key].push(value);
                                            }
                                        } else {
                                            if ( !amenities['more'] ) {
                                                amenities['more'] = [];
                                            }
                                            
                                            if (!amenities['more'].includes(value)) {
                                                amenities['more'].push(value);
                                            }
                                        }
                                    });
                                }
                            }
                        }

                        if (response.data.errors) {
                            self.errors = response.data.errors;
                        }
                    })
                ;

                axios
                    .get('https://api.stay.com.de/?action=hotel-rating' +
                        '&id=' + this.fromHex(this.$route.params.id) //+ 
                        //'&mode=' + process.env.VUE_APP_MODE
                    )
                    .then(response => {
                        if (response.data && response.data.meta.count > 0) {
                            this.sentiment = response.data.data[0];
                        }
                    })
                ;
            },
            convertToSlug(text) {
                return text
                    .toLowerCase()
                    .replace(/[^\w ]+/g,'')
                    .replace(/ +/g,'-')
                ;
            },
            toHex(a) {
                var s = unescape(encodeURIComponent(a))
                var h = ''
                for (var i = 0; i < s.length; i++) {
                    h += s.charCodeAt(i).toString(16)
                }
                return h
            },
            fromHex(h) {
                var s = ''
                for (var i = 0; i < h.length; i+=2) {
                    s += String.fromCharCode(parseInt(h.substr(i, 2), 16))
                }
                return decodeURIComponent(escape(s))
            },
            capitalize(s) {
                if (typeof s !== 'string') return '';
                return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
            },
            checkAm(item, obj) {
                 return obj.includes(item);
            },
            roomDetails: function(offer) {
                var type = offer.room.type;
                var typeEstimated = offer.room.typeEstimated;

                var run_of_house = false
                var room_type = {
                    'type': 'RUN_OF_HOUSE_ROOM',
                    'desr': '',
                }
                var number_of_beds = 1
                var bed_type = {
                    'type': 'RUN_OF_HOUSE_BED',
                    'persons': offer.guests.adults,
                }
                var accessible = false;

                if (typeEstimated !== undefined) {

                    if (typeEstimated.category == 'ACCESSIBLE_ROOM') {
                        accessible = true;
                    }

                    if (typeEstimated.category !== undefined) {
                        room_type = {
                            'type': typeEstimated.category,
                            'desr': '',
                        }
                    }

                    if (typeEstimated.beds !== undefined) {
                        number_of_beds =  typeEstimated.beds
                    }

                    if (
                        typeEstimated.bedType !== undefined
                    ) {
                        if (this.bed_type_mapping[typeEstimated.bedType[0]] !== undefined) {
                            bed_type = this.bed_type_mapping[typeEstimated.bedType[0]];
                        } else {
                            bed_type = {
                                'type': typeEstimated.bedType,
                                'persons': offer.guests.adults,
                            }
                        }
                    }
                }

                if (type !== undefined) {
                    // ROH: Run of the House
                    if (type == 'ROH') {
                        run_of_house = true
                    } else {
                        if (
                            type[0] !== undefined && 
                            type[1] !== undefined &&
                            (this.isNumeric(type[1]) || type[1] == '*') && 
                            type[2] !== undefined &&
                            this.room_type_mapping[type[0]] !== undefined &&
                            this.bed_type_mapping[type[2]] !== undefined
                        ) {
                            var room = type[0];
                            var number = type[1];
                            var bed = type[2];

                            if (number == '*' && number_of_beds <= 1) {
                                number = 1;
                            }
                            
                            room_type = this.room_type_mapping[room];
                            number_of_beds = number;
                            bed_type = this.bed_type_mapping[bed];
                        }
                    }
                }

                return {
                    'room_type': room_type,
                    'number_of_beds': number_of_beds,
                    'bed_type': bed_type,
                    'accessible': accessible,
                    'run_of_house': run_of_house,
                };
            },
            isNumeric: function(n) {
                return !isNaN(parseFloat(n)) && isFinite(n);
            },
        },
        i18n: { // `i18n` option, setup locale info for component
            messages: {
                en: {
                    bed: 'bed | beds',
                }
            },
        }
    }
</script>

<style>
    @media screen and (min-width:991px) {
        .search-wrapper .form-group{
            margin:0;
        }
    }
</style>
<style scoped>
    nav{
        z-index:10;
    }
    .position-sticky{
        top:0;
    }
    h3.position-sticky{
        top:55px;
    }
    .maps{
        width:100%;
        height:200px;
        border-radius:10px;
        overflow:hidden;
    }
    .inner{
        background-color:#fff;
    }
    .search-wrapper{
        background-color:#F3F3F3;
    }
    .section-menu-wrap{
        z-index:100;
    }

    .section-menu{
        border-bottom:1px solid #ddd;
    }
    .section-menu li{
        font-weight:bold;
    }
    
    #reviews .item{
        margin-bottom:5px;
    }

    .room-group{
        border-bottom:1px solid rgba(0, 0, 0, 0.1);
    }
    
</style>